import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { useWeeklyCompliance } from '@/hooks/useWeeklyReports';
import type { WeeklyComplianceProject, WeeklyComplianceState } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * T-21 / T-44a: weekly-report compliance for the leadership dashboard. Reports are due Friday
 * 4 PM CT and reviewed before the Monday PMO call, so the card offers the current week and the
 * previous one (the week under review on Monday).
 */

type WeekChoice = 'current' | 'previous';

export const COMPLIANCE_STATE_META: Record<
  WeeklyComplianceState,
  { label: string; variant: 'danger' | 'warning' | 'success' | 'info' }
> = {
  missing: { label: 'Missing', variant: 'danger' },
  pending_review: { label: 'Awaiting review', variant: 'info' },
  changes_requested: { label: 'Changes requested', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
};

/** Missing first (needs chasing), then awaiting review, changes requested, approved. */
const STATE_ORDER: WeeklyComplianceState[] = [
  'missing',
  'pending_review',
  'changes_requested',
  'approved',
];

/** YYYY-MM-DD of a local calendar date `days` before today. Any date in the week works. */
export function isoDaysAgo(days: number, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function sortComplianceProjects(
  projects: readonly WeeklyComplianceProject[],
): WeeklyComplianceProject[] {
  return [...projects].sort(
    (a, b) =>
      STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state) ||
      a.title.localeCompare(b.title),
  );
}

function formatDay(isoDate: string): string {
  // Date-only strings are parsed as UTC; render them as the same calendar day.
  return new Date(`${isoDate}T12:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function WeeklyComplianceCard(): JSX.Element {
  const [choice, setChoice] = useState<WeekChoice>('current');
  const week = choice === 'previous' ? isoDaysAgo(7) : undefined;
  const { data, isLoading, isError, error, refetch } = useWeeklyCompliance(week);

  const rows = useMemo(() => sortComplianceProjects(data?.projects ?? []), [data]);
  const submitted = data
    ? data.counts.pending_review + data.counts.changes_requested + data.counts.approved
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-base font-semibold text-text-primary">Weekly Report Compliance</h3>
            <p className="text-sm text-text-secondary">
              {data
                ? `Week of ${formatDay(data.week_start)} – ${formatDay(data.week_end)} · due ${new Date(data.due_at).toLocaleString()}`
                : 'Reports are due Friday 4 PM CT and reviewed before the Monday PMO call'}
            </p>
          </div>
          <div role="group" aria-label="Report week" className="flex gap-2">
            <Button
              size="sm"
              variant={choice === 'current' ? 'default' : 'outline'}
              aria-pressed={choice === 'current'}
              onClick={() => setChoice('current')}
            >
              This week
            </Button>
            <Button
              size="sm"
              variant={choice === 'previous' ? 'default' : 'outline'}
              aria-pressed={choice === 'previous'}
              onClick={() => setChoice('previous')}
            >
              Last week
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-40 w-full rounded-lg" />
        ) : isError ? (
          <Alert variant="destructive">
            <AlertTitle>Failed to load weekly report compliance</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              <span>{error instanceof Error ? error.message : 'An unexpected error occurred.'}</span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : !data || data.counts.expected === 0 ? (
          <p className="py-8 text-center text-sm text-text-secondary">
            No active projects expected to report this week.
          </p>
        ) : (
          <>
            <p className="text-sm text-text-primary" data-testid="compliance-summary">
              {submitted} of {data.counts.expected} submitted · {data.counts.missing} missing ·{' '}
              {data.counts.pending_review} awaiting review
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead>PM</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((p) => {
                  const meta = COMPLIANCE_STATE_META[p.state];
                  return (
                    <TableRow key={p.jira_key}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-text-primary">{p.title}</span>
                          <span className="font-mono text-xs text-text-secondary">{p.jira_key}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-text-secondary">{p.project_manager || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={meta.variant}>{meta.label}</Badge>
                      </TableCell>
                      <TableCell className="text-text-secondary">
                        {p.submitted_at ? new Date(p.submitted_at).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild variant="link" size="sm" className="gap-1">
                          <Link
                            to={`/projects/${p.jira_key}?tab=weekly-reports`}
                            aria-label={`Open weekly reports for ${p.title}`}
                          >
                            {p.state === 'pending_review' ? 'Review' : 'View'}
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default WeeklyComplianceCard;
